import { useEffect, useRef, useState } from 'react';
import { useApp } from '../state/AppState.jsx';
import { supabase, unwrap } from '../lib/supabase.js';
import { navigate } from '../lib/router.js';
import * as Icon from '../lib/icons.jsx';
import { Field, PasswordInput } from '../components/ui.jsx';

const POINTS = [
  ['Boards that bend to your process', 'Status, people, dates, timelines, files - add the columns your team actually uses.'],
  ['Four views over the same data', 'Table, Kanban, Calendar and Gantt, all live on the same items.'],
  ['See where the work really is', 'Workload by person, progress by board, and what is about to slip.'],
];

export default function AuthPage({ route }) {
  const app = useApp();
  const inviteMatch = route.match('/invite/:token');
  const [mode, setMode] = useState(route.path === '/signup' ? 'signup' : 'login');
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', title: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const firstField = useRef(null);

  useEffect(() => {
    // /signup is still a route because an invite link lands on it, but it is
    // not an entrance any more: without a token there is nothing to sign up to.
    if (route.path === '/signup') setMode(inviteMatch ? 'signup' : 'login');
    if (route.path === '/login') setMode('login');
  }, [route.path, inviteMatch]);

  // An invite link decides the mode for you: join if you already have an account,
  // sign up otherwise, with the email locked to the address that was invited.
  useEffect(() => {
    if (!inviteMatch) return;
    (async () => {
      try {
        // get_invite is SECURITY DEFINER and granted to anon: this is the one
        // read that has to work before the visitor has an account.
        const found = unwrap(await supabase.rpc('get_invite', { p_token: inviteMatch.token }));
        if (!found) throw new Error('That invite link is not valid or has already been used');
        setInvite(found);
        setForm((current) => ({ ...current, email: found.email }));
        setMode(found.hasAccount ? 'login' : 'signup');
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [inviteMatch?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { firstField.current?.focus(); }, [mode]);

  const update = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  /**
   * Emails a sign-in link that lets you set a new password.
   *
   * The response is deliberately the same whether or not the address has an
   * account. Telling an anonymous visitor "no such user" turns this box into a
   * way to find out who works here.
   */
  const sendReset = async () => {
    if (!form.email.trim()) {
      setError('Enter your email address first, then ask for a reset link.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      unwrap(await supabase.auth.resetPasswordForEmail(form.email.trim(), {
        redirectTo: `${window.location.origin}/`,
      }));
      app.toast('If that address has an account, a reset link is on its way', 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try {
      if (mode === 'signup') {
        await app.signUp({ ...form, inviteToken: inviteMatch?.token });
      } else {
        // Handed to signIn rather than redeemed afterwards, so the invite is
        // accepted before workspaces load and no stray personal workspace is
        // bootstrapped for someone who was invited to an existing one.
        await app.signIn({
          email: form.email,
          password: form.password,
          inviteToken: inviteMatch?.token,
        });
      }
      app.toast(mode === 'signup' ? 'Welcome to Pulse' : 'Signed in', 'success');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth-side">
        <div className="row-flex" style={{ gap: 10 }}>
          <span className="brand-mark" style={{ background: 'rgba(255,255,255,0.16)' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 13h4l3-8 4 15 3-9h4" />
            </svg>
          </span>
          <strong style={{ fontSize: 16 }}>Pulse</strong>
        </div>

        <div>
          <h1 className="auth-quote">Every project, every deadline, every person, on one surface.</h1>
          <div className="auth-points">
            {POINTS.map(([title, text]) => (
              <div className="auth-point" key={title}>
                <Icon.Check size={17} style={{ marginTop: 2, flex: 'none' }} />
                <span><strong>{title}.</strong> {text}</span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ fontSize: 12.5, opacity: 0.75 }}>Project management and team collaboration</p>
      </div>

      <div className="auth-form-wrap">
        <form className="auth-form" onSubmit={submit}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {invite
                ? `Join ${invite.workspaceName}`
                : mode === 'signup' ? 'Create your account' : 'Welcome back'}
            </h2>
            <p style={{ color: 'var(--text-2)', marginTop: 4, fontSize: 13.5 }}>
              {invite
                ? `${invite.invitedByName} invited you as a ${invite.role}.`
                : mode === 'signup'
                  ? 'Set up a workspace in under a minute.'
                  : 'Sign in to pick up where you left off.'}
            </p>
          </div>

          {mode === 'signup' && (
            <>
              <Field label="Full name">
                {(id) => (
                  <input id={id} ref={firstField} className="input" value={form.name}
                    onChange={update('name')} placeholder="Alex Morgan" autoComplete="name" required />
                )}
              </Field>
              <Field label="Job title" hint="Optional. Shown next to your name.">
                {(id) => (
                  <input id={id} className="input" value={form.title}
                    onChange={update('title')} placeholder="Product Manager" />
                )}
              </Field>
            </>
          )}

          <Field label="Work email">
            {(id) => (
              <input
                id={id}
                ref={mode === 'login' ? firstField : undefined}
                className="input"
                type="email"
                value={form.email}
                onChange={update('email')}
                placeholder="you@company.com"
                autoComplete="email"
                disabled={!!invite}
                required
              />
            )}
          </Field>

          <Field
            label="Password"
            hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
            error={error}
          >
            {(id) => (
              <PasswordInput
                id={id}
                value={form.password}
                onChange={update('password')}
                placeholder="••••••••"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
              />
            )}
          </Field>

          <button type="submit" className="btn primary lg block" disabled={busy}>
            {busy ? <span className="spinner" /> : invite
              ? 'Join workspace'
              : mode === 'signup' ? 'Create account' : 'Sign in'}
          </button>

          {mode === 'login' && (
            <p className="auth-switch">
              <button type="button" className="link-btn" onClick={sendReset} disabled={busy}>
                Forgot your password?
              </button>
            </p>
          )}

          {/*
            No "create an account" link. Sign-up is by invitation: an admin
            invites an address from the People page, and the link in that email
            is the only way in. The database enforces it too - see
            signup_allowed() in migration 0014 - because a hidden form stops
            nobody from posting to /auth/v1/signup directly.
          */}
          {!invite && (
            <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', margin: 0 }}>
              New to Pulse? Accounts are created by invitation. Ask your admin to
              send you a link.
            </p>
          )}

          {/*
            The demo-credentials block lived here. It advertised demo@pulse.app /
            pulse1234, which came from the Express seed script - accounts that do
            not exist on a clean Supabase install, so the button guaranteed a
            failed sign-in. Removed rather than reworded: on a vanilla install
            there is no demo workspace to point at.
          */}
        </form>
      </div>
    </div>
  );
}
